import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Pipeline {
  id: string
  name: string
  stages: number
  schedule: string
  last_run: string
  status: string
  duration: string
}

interface PipelineRun {
  run_id: string
  pipeline: string
  started: string
  status: string
  duration: string
}

interface Statistics {
  total_pipelines: number
  active_pipelines: number
  runs_today: number
  runs_mtd: number
  success_rate: number
  avg_duration_min: number
}

export default function MLPipelinePage() {
  const [pipelines, setPipelines] = useState<Pipeline[]>([])
  const [runs, setRuns] = useState<PipelineRun[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [pipelinesRes, runsRes, statsRes] = await Promise.all([
        fetch("/api/mlpipeline/pipelines"),
        fetch("/api/mlpipeline/runs"),
        fetch("/api/mlpipeline/statistics")
      ])
      if (pipelinesRes.ok) setPipelines(await pipelinesRes.json())
      if (runsRes.ok) setRuns(await runsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error("Error fetching ML pipeline:", error)
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat("en-US").format(value)
  }

  const getStatusBadge = (status: string) => {
    if (status === "Success") return "bg-green-100 text-green-800"
    if (status === "Running") return "bg-blue-100 text-blue-800"
    if (status === "Failed") return "bg-red-100 text-red-800"
    return "bg-gray-100 text-gray-800"
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">ML Pipeline</h1>
          <p className="mt-2 text-gray-600">Phase 277 - End-to-end ML pipeline orchestration</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Pipelines</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_pipelines}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active</p>
              <p className="text-2xl font-bold text-green-600">{statistics.active_pipelines}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Runs Today</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.runs_today}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Runs MTD</p>
              <p className="text-2xl font-bold text-purple-600">{formatNumber(statistics.runs_mtd)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Success Rate</p>
              <p className="text-2xl font-bold text-green-600">{statistics.success_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Duration</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.avg_duration_min}m</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Recent Runs</h2>
          </div>
          <div className="p-6 space-y-4">
            {runs.map((run) => (
              <div key={run.run_id} className="p-4 bg-gray-50 rounded-lg flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{run.pipeline}</p>
                  <p className="text-sm text-gray-500">{run.started}</p>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-gray-500">{run.duration}</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(run.status)}`}>{run.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Pipelines</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pipeline</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stages</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Schedule</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Run</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pipelines.map((pipe) => (
                  <tr key={pipe.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{pipe.name}</td>
                    <td className="px-6 py-4 text-blue-600">{pipe.stages}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">{pipe.schedule}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{pipe.last_run}</td>
                    <td className="px-6 py-4 text-gray-500">{pipe.duration}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(pipe.status)}`}>{pipe.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
